/**
 * フローの画面の枠。真ん中に図、右端に詳細、上に表示の操作列を置く。
 *
 * **枠だけをここに置き、中身は差し込ませる。** 業務フローと業務ロジックの
 * フローで変わるのは、図の配置ルールと詳細の中身だけである。枠（どこへ図を置くか・詳細を
 * どちらへ回すか・全画面にどう入るか・器をどう測るか）は同じなのに、画面ごとに手で書くと
 * 必ず食い違う。実際にそうなっていた — マスターから見た図は詳細が右に、PJ から見た図は下に
 * 出て、同じ「節点を選ぶと説明が出る」体験が画面によって別物になっていた。
 *
 * **器の幅を測ってから図を描く。** 初期表示の視点は最初の描画でしか読まれないので、幅を
 * 知らないまま描くと「どこへ寄せるか」を決められない。測る対象は図を収める器 1 つだけにする —
 * 左右に並べる 2 枚は同じ割り付けで同じ幅なので、2 つ測ると同じ事実の 2 表現になる。
 *
 * **縮尺と視点の同期も枠が決めて面へ配る。** 縮尺は図の幅と面の幅から 1 つに決まり、左右比較では
 * 両面に同じ値を当てる。面を組み立てる画面の部品を中継させず、文脈（`FlowPaneContext`）で
 * 図の面へ直接届ける。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フローの画面の枠として、図・詳細・表示の操作列・全画面・器の実測を 1 箇所に閉じる。
 * @graph-connects FlowWithDetailLayout [calls] 図と詳細の並べ方
 * @graph-connects useFlowFullscreen [calls] 全画面の状態
 * @graph-connects useMeasuredWidth [calls] 図の器の表示幅の実測
 * @graph-connects useViewportHeight [calls] 画面の高さの実測
 * @graph-connects fitScaleOf [calls] 初期表示の縮尺
 * @graph-connects createViewportSync [calls] 左右 2 面の視点の同期
 */
import { useMemo, useState, type ReactNode } from 'react';

import { DiffCompareToggle } from '../../components/spec/DiffSplitPanes.js';
import {
  FlowFullscreenFrame,
  FlowFullscreenToggle,
  useFlowFullscreen,
} from '../flow-fullscreen.js';
import { FlowWithDetailLayout } from '../flow-layout.js';
import { useMeasuredWidth } from '../flow-pane-width.js';
import { useViewportHeight } from '../flow-viewport-height.js';
import { canvasHeightOf } from '../user-story-flow-layout.js';
import {
  createViewportSync,
  FlowPaneContext,
  type FlowPaneContextValue,
} from './flow-pane-context.js';
import { COMPARE_PANE_GAP, fitScaleOf, paneWidthOf } from './flow-scale.js';

/**
 * 図を描くときに枠から渡る寸法。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 枠が実測した図の器の幅と高さを表す。
 * @graph-connects none
 */
export interface FlowPaneFrame {
  /** 図 1 面の表示幅。左右比較では器の幅の半分。初期表示の視点をここから決める。 */
  paneWidth: number;
  /** 図の器の高さ。縮尺を当てた図の高さ・全画面かどうか・画面の高さから決まる。 */
  height: number;
  /** 初期表示の縮尺。図の面（`FlowCanvas`）が自分で当てるので、実寸の視点を作るときに割り戻す。 */
  scale: number;
}

/**
 * 左右比較の状態と切り替えの口。比較を持たない画面では渡さない。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 枠が受け取る左右比較の状態と切り替えの口を表す。
 * @graph-connects none
 */
export interface FlowWorkspaceCompare {
  /** 変更前と並べているか。 */
  split: boolean;
  /** 変更前と変更後に差が無く、切り替えのボタンを押せないか。 */
  disabled: boolean;
  /** 押したときに切り替える。 */
  onToggle: () => void;
  /** 比べている案件の番号。変更後の面の見出しに出す。 */
  pjNumber: number;
  /** 切り替えのボタンを指したときに出す断り（1 面の表示に出せない削除の件数など）。省けば出さない。 */
  title?: string;
}

/**
 * `FlowWorkspace` の props。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フローの枠に渡す図の面・詳細・左右比較・断り書き・差し替え・凡例を表す。
 * @graph-connects none
 */
export interface FlowWorkspaceProps {
  /** 図の配置の高さ。器の高さをここから決める。左右比較では高いほう。 */
  layoutHeight: number;
  /** 図の配置の幅。初期表示の縮尺をここから決める。左右比較では広いほう。 */
  canvasWidth: number;
  /** 図の面。器の幅が測れてから呼ばれる。左右比較では 2 枚を返す。 */
  panes: (frame: FlowPaneFrame) => ReactNode;
  /**
   * 右端（左右比較では図の下）に置く詳細。省けば詳細の列を取らず、図が幅いっぱいを使う
   * （業務フローの図は節点を押すまで右パネルを閉じる）。
   */
  detail?: ReactNode;
  /**
   * 図の枠を静かにするか。1 面の見出しを出さず、全画面・拡大・縮小のボタンを図に乗った時だけ出す。
   * 省けば出す（業務のデータの流れの図）。
   */
  quiet?: boolean;
  /** 操作列の左端に置く ⓘ（図の読み方の補足）。省けば置かない。 */
  info?: ReactNode;
  /**
   * 左右比較の状態。渡さない画面には切り替えのボタンを出さず、図は 1 枚のままになる。
   *
   * 省略できる形にしてあるのは、比較できるかどうかが画面の性質だからである。マスター 1 枚を
   * 読む画面には比べる相手が無い。
   */
  compare?: FlowWorkspaceCompare;
  /** 操作列の下に出す断り書き（1 枚の表示に出せない要素の件数など）。 */
  notice?: ReactNode;
  /**
   * 図を 1 枚も描けないときに、操作列ごと差し替えて出すもの。
   *
   * 拡大も比較もできない面に操作列だけを残すと、押しても何も起きないボタンが並ぶ。
   */
  placeholder?: ReactNode;
  /** 図の器の外、詳細の手前に置くもの（凡例など）。 */
  footer?: ReactNode;
}

/**
 * フローの画面の枠を描く。
 *
 * @param props 配置の高さ・図の面・詳細・左右比較・断り書き・差し替え・凡例・静かな枠・ⓘ
 * @returns 図と詳細を並べた枠
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フローの図と詳細を並べ、全画面・左右比較・器の実測を枠として与え、静かな枠では操作のボタンを図に乗った時だけ出す。
 * @graph-connects FlowWithDetailLayout [calls] 図と詳細の並べ方
 * @graph-connects useFlowFullscreen [calls] 全画面の状態
 * @graph-connects canvasHeightOf [calls] 図の器の高さの決定
 * @graph-connects fitScaleOf [calls] 初期表示の縮尺
 * @graph-connects paneWidthOf [calls] 図 1 面の幅
 * @graph-connects createViewportSync [calls] 左右 2 面の視点の同期
 * @graph-connects QuietGroup [calls] 静かな枠の操作列と図を乗った判定の組にまとめる
 */
export function FlowWorkspace({
  layoutHeight,
  canvasWidth,
  panes,
  detail,
  compare,
  notice,
  placeholder,
  footer,
  quiet = false,
  info,
}: FlowWorkspaceProps) {
  const { fullscreen, toggle: toggleFullscreen } = useFlowFullscreen();
  const viewportHeight = useViewportHeight();
  const { width: frameWidth, measured, attach: attachMeasure } = useMeasuredWidth();

  /* 比較を持たない画面は 1 枚で固定される。詳細の置き場所もそれに従う。 */
  const split = compare !== undefined && compare.split;
  /*
   * 縮尺は両面で 1 つ。面ごとに自分の幅で縮めると、同じ業務が左右で違う大きさに描かれる。
   * 器の高さも縮めた図に合わせる。実寸の高さのまま縮めると、図の下に縮めた分の空白が残る。
   */
  /*
   * 面の幅は図の器を測った値を採る。格子の幅から割り出した値は、図の器を包む区画の余白の
   * ぶん広く、縮尺が式の値より大きく出る。器を測るまでは割り出した値で面を描き、図の面は
   * 器を測って知らせるまで React Flow を描かない（最初の視点は描いた時点でしか読まれない）。
   */
  const [reportedPaneWidth, setReportedPaneWidth] = useState<number | null>(null);
  const paneWidth = reportedPaneWidth ?? paneWidthOf(frameWidth, split);
  const scale = fitScaleOf(paneWidth, canvasWidth);
  const height = canvasHeightOf(layoutHeight * scale, { fullscreen, height: viewportHeight });
  /*
   * 同期の口は枠が生きている間 1 つだけ作る。描くたびに作り直すと、登録した面が古い口に
   * 残って片方へしか写らない。
   */
  const sync = useMemo(() => createViewportSync(), []);
  const comparePj = split ? compare.pjNumber : null;
  const paneMeasured = reportedPaneWidth !== null;
  const paneContext = useMemo<FlowPaneContextValue>(
    () => ({
      scale,
      reportPaneWidth: setReportedPaneWidth,
      paneMeasured,
      comparePj,
      sync: comparePj === null ? null : sync,
      quiet,
    }),
    [scale, paneMeasured, comparePj, sync, quiet],
  );

  return (
    /*
      詳細は既定で図の右に置く。左右比較のときだけ下へ回すのは、右に 400px を取ると図 1 枚が
      業務 2 個分の幅しか持てず、並べて比べる用途に足りないため。
    */
    <FlowWithDetailLayout
      detailBelow={split}
      diagram={
        <>
          {placeholder === undefined ? (
            <FlowFullscreenFrame fullscreen={fullscreen}>
              <QuietGroup quiet={quiet}>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {info !== undefined && <div className="mr-auto flex items-center">{info}</div>}
                  {/*
                  切り替えのボタンは表や一覧の比較と同じ部品を使う。同じ操作が画面ごとに違う
                  名前や見た目で出ると、読み手は画面を移るたびに「これはさっきの比較と同じものか」を
                  確かめることになる。押し下げの表し方（1 つのボタン + `aria-pressed`）も部品が持つ。
                */}
                  {compare !== undefined && (
                    <DiffCompareToggle
                      split={compare.split}
                      disabled={compare.disabled}
                      onToggle={compare.onToggle}
                      {...(compare.title === undefined ? {} : { title: compare.title })}
                    />
                  )}
                  {/*
                  静かな枠では、全画面のボタンを図に乗った時（とキーボードで辿った時）だけ出す。
                  全画面の間は出したままにする（終わる手段が見えないと抜けられない）。
                */}
                  {quiet && !fullscreen ? (
                    <span className={QUIET_TOGGLE_CLASS} data-quiet-toggle="">
                      <FlowFullscreenToggle fullscreen={fullscreen} onToggle={toggleFullscreen} />
                    </span>
                  ) : (
                    <FlowFullscreenToggle fullscreen={fullscreen} onToggle={toggleFullscreen} />
                  )}
                </div>
                {notice}
                {/*
                幅を測るのは図を収める器 1 つだけ。左右に並べる 2 枚は同じ割り付けで同じ幅なので、
                測る対象を 2 つにすると同じ事実の 2 表現になる。
              */}
                {/*
                左右比較は 2 面を同じ幅（`1fr 1fr`）で並べる。画面幅で 1 列へ畳むと、比べる
                相手が縦に離れて見比べられない。狭い画面は枠ごと横へ動かして読む。
              */}
                <div
                  ref={attachMeasure}
                  className={split ? 'grid min-w-0 grid-cols-2' : 'grid min-w-0'}
                  style={{ gap: COMPARE_PANE_GAP }}
                >
                  {/*
                  器の幅が測れてから図を描く。初期表示の視点は最初の描画でしか読まれないので、
                  幅を知らないまま描くと寄せ先を決められない。
                */}
                  <FlowPaneContext.Provider value={paneContext}>
                    {measured && panes({ paneWidth, height, scale })}
                  </FlowPaneContext.Provider>
                </div>
              </QuietGroup>
            </FlowFullscreenFrame>
          ) : (
            placeholder
          )}
          {footer}
        </>
      }
      detail={detail}
    />
  );
}

/** 静かな枠の全画面のボタン。図に乗った時とキーボードで辿った時だけ出す。 @graph-connects none */
const QUIET_TOGGLE_CLASS =
  'opacity-0 transition-opacity group-hover/flow:opacity-100 focus-within:opacity-100';

/**
 * 静かな枠のとき、操作列と図を 1 つの「乗った」判定の組にまとめる（箱は作らない）。
 *
 * 静かでない枠では何も足さない。足すと業務のデータの流れの図の要素の並びが変わる。
 *
 * @param props 静かな枠か・中身
 * @returns 組にまとめた中身
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 静かな枠の操作列と図を、図に乗った時にボタンを出す判定の組にまとめる。
 * @graph-connects none
 */
function QuietGroup({ quiet, children }: { quiet: boolean; children: ReactNode }) {
  return quiet ? <div className="group/flow contents">{children}</div> : children;
}
